import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import api from '../utils/api';
import './AdminTicketManager.css';

// Admin-side queue for tickets filed by PHONE (refund requests today, anything
// else later). Read-only review + resolve/reject with an optional note.
// PHONE itself never auto-mutates user funds — release_escrow_for_order is
// admin-driven from the chat panel separately.

const STATUS_LABELS = { open: 'Open', resolved: 'Resolved', rejected: 'Rejected' };

const AdminTicketManager = () => {
  const [status, setStatus] = useState('open');
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [noteById, setNoteById] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/phoneai/admin/tickets?status=${encodeURIComponent(status)}`);
      setTickets(res.data?.tickets || []);
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || 'Failed to load tickets';
      toast.error(msg);
      setTickets([]);
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => { load(); }, [load]);

  const act = useCallback(async (id, action) => {
    if (busyId) return;
    setBusyId(id);
    try {
      const note = String(noteById[id] || '').slice(0, 1000);
      await api.post(`/phoneai/admin/tickets/${id}/${action}`, { note });
      toast.success(`Ticket ${action}d.`);
      setNoteById((m) => { const n = { ...m }; delete n[id]; return n; });
      load();
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || `Failed to ${action} ticket`;
      toast.error(msg);
    } finally {
      setBusyId(null);
    }
  }, [busyId, noteById, load]);

  return (
    <div className="ticket-manager">
      <div className="ticket-manager-header">
        <div className="ticket-manager-title">PHONE Tickets</div>
        <div className="ticket-manager-actions">
          <select
            className="ticket-manager-select"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            {Object.entries(STATUS_LABELS).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
          <button className="ticket-manager-btn" onClick={load} disabled={loading}>
            {loading ? 'Loading…' : 'Refresh'}
          </button>
        </div>
      </div>

      {loading && tickets.length === 0 && (
        <div className="ticket-manager-empty">Loading…</div>
      )}
      {!loading && tickets.length === 0 && (
        <div className="ticket-manager-empty">No {STATUS_LABELS[status].toLowerCase()} tickets.</div>
      )}

      <div className="ticket-list">
        {tickets.map((t) => {
          const isOpen = t.status === 'open';
          const p = t.payload || {};
          return (
            <div key={t.id} className={`ticket-card ticket-card--${t.status}`}>
              <div className="ticket-card-head">
                <div className="ticket-card-type">
                  {t.type === 'refund' ? '↩ Refund request' : t.type}
                </div>
                <div className="ticket-card-status">{STATUS_LABELS[t.status] || t.status}</div>
              </div>

              <div className="ticket-card-meta">
                <div><strong>Wallet:</strong> <code>{t.wallet}</code></div>
                <div><strong>Filed:</strong> {new Date(t.created_at).toLocaleString()}</div>
                {t.resolved_at && (
                  <div>
                    <strong>{STATUS_LABELS[t.status]}:</strong> {new Date(t.resolved_at).toLocaleString()}
                    {t.resolved_by ? <> by <code>{t.resolved_by}</code></> : null}
                  </div>
                )}
              </div>

              {t.type === 'refund' && (
                <div className="ticket-card-payload">
                  <div><strong>Order:</strong> #{p.provider_order_id} <span style={{opacity:0.7}}>(db {p.order_db_id})</span></div>
                  {p.service_name && <div><strong>Service:</strong> {p.service_name}</div>}
                  {p.charged_usd != null && <div><strong>Charged:</strong> ${Number(p.charged_usd).toFixed(2)}</div>}
                  {p.escrow_order_id && <div><strong>Escrow id:</strong> <code>{p.escrow_order_id}</code></div>}
                  {p.reason && <div className="ticket-card-reason"><strong>Reason:</strong> {p.reason}</div>}
                </div>
              )}

              {!t.payload?.reason && t.type !== 'refund' && (
                <pre className="ticket-card-rawpayload">{JSON.stringify(t.payload, null, 2)}</pre>
              )}

              {t.admin_note && (
                <div className="ticket-card-note"><strong>Admin note:</strong> {t.admin_note}</div>
              )}

              {isOpen && (
                <>
                  <textarea
                    className="ticket-card-noteinput"
                    placeholder="Optional note (visible in the ticket history)…"
                    value={noteById[t.id] || ''}
                    onChange={(e) => setNoteById((m) => ({ ...m, [t.id]: e.target.value }))}
                    maxLength={1000}
                  />
                  <div className="ticket-card-actions">
                    <button
                      className="ticket-manager-btn ticket-manager-btn--ghost"
                      disabled={busyId === t.id}
                      onClick={() => act(t.id, 'reject')}
                    >
                      Reject
                    </button>
                    <button
                      className="ticket-manager-btn ticket-manager-btn--primary"
                      disabled={busyId === t.id}
                      onClick={() => act(t.id, 'resolve')}
                    >
                      Mark resolved
                    </button>
                  </div>
                  {t.type === 'refund' && (
                    <div className="ticket-card-hint">
                      To actually refund: open PHONE chat and ask "release escrow for order #{p.provider_order_id}".
                    </div>
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AdminTicketManager;
